import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, Signal, computed } from '@angular/core';
import { DateTime, Duration } from 'luxon';
import { Observable, map, takeWhile, tap, timer } from 'rxjs';
import { CountDownFormatPipe } from './countdown-format.pipe';

/**
 * Countdown version 2 of the countdown component, this component uses luxon for date time manipulation.
 * This is more accurate than the previous countdown component as this one sets the millisecond to 0 while
 * the old component uses math.floor to get the remaining time and completes sometimes ahead of 1 second.
 * When the old component triggers complete when comparing the start date and now date again, the remaining time is not accurate as sometimes the result
 * will be behind by 1 second resulting to false
 * @example <fgbcl-countdown-v2 [endDateTime]="comingSoonDate" (complete)="itemStart.emit()"></fgbcl-countdown-v2>
 *
 */
@Component({
  selector: 'fgbcl-countdown-v2',
  standalone: true,
  imports: [CommonModule, CountDownFormatPipe],
  templateUrl: './countdown-v2.component.html',
  styleUrl: './countdown-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountdownV2Component implements OnInit {
  @Input() startDateTime: string;
  /** The end date time for the countdown in UTC format. */
  @Input({ required: true }) endDateTime: string;
  /** The text to display in the countdown upon expiry. */
  @Input() completeText: string = 'Complete';

  /** determines whether component will receive local or utc time. default is utc. */
  @Input() isUTC: boolean = true;

  /** Emitted upon countdown expiry. */
  @Output() complete = new EventEmitter<any>();
  now: DateTime;

  endDate: Signal<DateTime> = computed(() =>
    this.isUTC ? DateTime.fromISO(this.endDateTime, { zone: 'utc' }) : DateTime.fromISO(this.endDateTime)
  );
  remainingTime$: Observable<any>;
  completed = false;

  ngOnInit(): void {
    this.now = this.isUTC ? DateTime.utc() : DateTime.now();

    if (this.startDateTime) {
      this.now = this.isUTC ? DateTime.fromISO(this.startDateTime, { zone: 'utc' }) : DateTime.fromISO(this.startDateTime);
    }

    this.startTimer();
  }

  startTimer(): void {
    const remainingTime = this.endDate()
      .diff(this.now.set({ millisecond: 0 }))
      .toObject();
    const remainingTimeInMillis = Duration.fromObject(remainingTime).as('milliseconds');

    this.remainingTime$ = timer(0, 1000).pipe(
      map((x) => Math.max(0, Math.floor((remainingTimeInMillis - x * 1000) / 1000)) * 1000), // Ensure rounding
      takeWhile((milliseconds) => milliseconds > 0, true), // Include the last tick at 0
      tap((milliseconds) => {
        if (milliseconds <= 0) {
          this.completed = true;
          this.complete.emit(this.completed);
        }
      })
    );
  }
}
